import React from 'react'

import { AgentSourcedCreatorStatus, DecidedCreatorStatus } from '../types'
import { PillRoot, PillUndoButton, StatusLabel } from './VibeCheckStatusPill.styled'

interface IProps {
  status: AgentSourcedCreatorStatus
  onUndo?: () => void
}

// BRAND-9587: this was a binary `isApproved ? 'Approved' : 'Rejected'`, so every
// status that was not literally `approved` fell through to "Rejected" — including
// `ready_for_shortlist`, the state an APPROVED creator lands in after answering
// "Interested". Keyed maps make the build fail on the next new BE status instead
// of quietly mislabelling it.
const STATUS_LABEL: Record<DecidedCreatorStatus, string> = {
  approved: 'Approved',
  ready_for_shortlist: 'Approved',
  rejected: 'Rejected',
}

// Undo is a vibe-check decision affordance. Once the creator's intent link is
// OPENED the decision is no longer reversible from here, so the pill is locked
// even if a caller passes `onUndo`. The BE is the authority: `undoable?` gates on
// `intent.link_generated?`, so it stops emitting the undo link the moment the
// intent leaves `link_generated` — before the creator answers, not after
// (BRAND-9680). Excluding `ready_for_shortlist` here is the client-side belt to
// those braces: that status is always well past the cutoff.
type UndoableStatus = 'approved' | 'rejected'

const isUndoable = (status: DecidedCreatorStatus): status is UndoableStatus =>
  status === 'approved' || status === 'rejected'

const UNDO_ARIA_LABEL: Record<UndoableStatus, string> = {
  approved: 'Undo approval',
  rejected: 'Undo rejection',
}

/**
 * Shows a read-only status pill for creators with a vibe-check outcome, with an
 * inline Undo icon button where undo is still legal. Renders null for pending.
 */
const VibeCheckStatusPill: React.FC<IProps> = ({ status, onUndo }) => {
  if (status === 'pending') {
    return null
  }

  const labelText = STATUS_LABEL[status]

  return (
    <PillRoot status={status} data-testid={`vibe-check-status-pill-${status}`}>
      <StatusLabel>{labelText}</StatusLabel>
      {onUndo && isUndoable(status) && (
        <PillUndoButton
          type="button"
          onClick={onUndo}
          aria-label={UNDO_ARIA_LABEL[status]}
          data-testid="vibe-check-pill-undo"
          data-tooltip-id="base-tooltip"
          data-tooltip-content="Undo"
          data-tooltip-place="bottom"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M2 6h7.5a4 4 0 1 1 0 8H6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 3 2 6l3 3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </PillUndoButton>
      )}
    </PillRoot>
  )
}

export { VibeCheckStatusPill }
export default VibeCheckStatusPill
